import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import * as satellite from 'satellite.js';
import axios from 'axios';
import { trajectoryCalculator } from '../utils/trajectoryCalculator';
import { nasaSatelliteService } from '../utils/nasaSatelliteService';

// Configure Cesium for development environment
window.CESIUM_BASE_URL = '/cesium/';
Cesium.buildModuleUrl.setBaseUrl('/cesium/');

// Set Cesium Ion access token
Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI4Mzk3NDRkOC1jM2YwLTQ2ZDgtODNhMy0yNDE1N2RkNDJlZGQiLCJpZCI6MzQwODk5LCJpYXQiOjE3NTc3MjAxMjF9.TeWYZ8SQ_t-QaO8Si48fFwPqOk5IhBtvP4syuEg9ef0';

const GlobeVisualizer = () => {
  const viewerRef = useRef(null);
  const viewerInstanceRef = useRef(null);
  const satellitesRef = useRef([]); // Use ref to avoid stale closure issues
  const [satellites, setSatellites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [nextRefreshTime, setNextRefreshTime] = useState(null);
  const [dataFetchError, setDataFetchError] = useState(false);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  // Always reset to 'all' on page load
  const getInitialFilter = () => {
    // Clear any existing filter from localStorage
    localStorage.removeItem('satelliteFilter');
    console.log('🗑️ Cleared localStorage filter, resetting to "all"');
    return 'all';
  };

  const [satelliteFilter, setSatelliteFilter] = useState(getInitialFilter); // Start with all satellites
  const [selectedSatellite, setSelectedSatellite] = useState(null);
  const [trajectoryEntity, setTrajectoryEntity] = useState(null);
  const [launchTrajectories, setLaunchTrajectories] = useState([]);
  const [selectedLaunchTrajectory, setSelectedLaunchTrajectory] = useState(null);
  const [trajectoryMode, setTrajectoryMode] = useState(false);
  const [renderingSatellites, setRenderingSatellites] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [satelliteBatchSize] = useState(500); // Start with smaller batches
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [isProgressiveLoading, setIsProgressiveLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false); // Prevent duplicate API calls
  const [isLiveUpdating, setIsLiveUpdating] = useState(false); // Separate state for live updates
  const firstUpdateCompletedRef = useRef(false); // Use ref to track first update completion
  const [viewerReady, setViewerReady] = useState(false); // Track when Cesium viewer is fully ready

  // Update frequency options (in milliseconds)
  const UPDATE_FREQUENCIES = {
    '30s': 30 * 1000,      // 30 seconds
    '60s': 60 * 1000,      // 60 seconds
    '5m': 5 * 60 * 1000,   // 5 minutes
    '30m': 30 * 60 * 1000  // 30 minutes
  };

  const [updateFrequency, setUpdateFrequency] = useState('5m'); // Default to 5 minutes

  // Local fallback TLE data (sample satellites for demonstration)
  const getFallbackTLEData = () => {
    return `ISS (ZARYA)
1 25544U 98067A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 25544  51.6400  90.0000 0002000   0.0000 000.0000 15.50000000    01
HUBBLE SPACE TELESCOPE
1 20580U 90037B   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 20580  28.4700  90.0000 0001000   0.0000 000.0000 15.00000000    02
GPS BIIR-2  (PRN 13)
1 24876U 97035A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 24876  55.0000  90.0000 0002000   0.0000 000.0000  2.00000000    03
NOAA 18
1 28654U 05018A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 28654  99.0000  90.0000 0001000   0.0000 000.0000 14.00000000    04
TERRA
1 25994U 99068A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 25994  98.2000  90.0000 0001000   0.0000 000.0000 14.50000000    05
AQUA
1 27424U 02022A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 27424  98.2000  90.0000 0001000   0.0000 000.0000 14.50000000    06
SUOMI NPP
1 37849U 11061A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 37849  98.7000  90.0000 0001000   0.0000 000.0000 14.20000000    07
LANDSAT 8
1 39084U 13008A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 39084  98.2000  90.0000 0001000   0.0000 000.0000 14.60000000    08
STARLINK-1007
1 44713U 19074A   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 44713  53.0000  90.0000 0002000   0.0000 000.0000 15.00000000    09
STARLINK-1008
1 44714U 19074B   25090.50000000  .00000000  00000-0  00000-0 0  9999
2 44714  53.0000  90.0000 0002000   0.0000 000.0000 15.00000000    10`;
  };

  // Check if refresh is allowed (3 hour cooldown)
  const canRefreshData = () => {
    if (!lastFetchTime) return true;
    const now = new Date();
    const timeSinceLastFetch = now - lastFetchTime;
    const threeHoursInMs = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
    return timeSinceLastFetch >= threeHoursInMs;
  };

  // Get time until next refresh is available
  const getTimeUntilNextRefresh = () => {
    if (!lastFetchTime) return null;
    const now = new Date();
    const timeSinceLastFetch = now - lastFetchTime;
    const threeHoursInMs = 3 * 60 * 60 * 1000;
    const remainingTime = threeHoursInMs - timeSinceLastFetch;
    return remainingTime > 0 ? remainingTime : 0;
  };

  // Fetch TLE data with caching and performance optimizations
  const fetchTLEData = async () => {
    // Prevent duplicate API calls
    if (isFetchingData) {
      console.log('Already fetching data, skipping duplicate request');
      return;
    }

    setIsFetchingData(true);

    // PERFORMANCE MONITORING: Start timing
    const startTime = performance.now();
    console.log('🚀 Starting TLE data fetch...');

    try {
      // Check if API is marked as unavailable (circuit breaker)
      if (apiUnavailable) {
        console.log('API service is currently unavailable, skipping fetch');
        setDataFetchError(true);
        setLoading(false);
        return;
      }

      // FIRST: Try to load from cache (unless forced refresh)
      const cachedData = localStorage.getItem('tleData');
      if (cachedData && !window.location.search.includes('refresh=true')) {
        try {
          const parsedCache = JSON.parse(cachedData);
          const cacheAge = new Date() - new Date(parsedCache.timestamp);
          const cacheValidHours = 6; // Cache valid for 6 hours

          if (cacheAge < (cacheValidHours * 60 * 60 * 1000)) {
            console.log(`✅ Using cached satellite data (${parsedCache.satellites.length} satellites, ${(cacheAge / (1000 * 60 * 60)).toFixed(1)}h old)`);

            // Even with cached data, we should enhance with NASA data
            console.log('🌌 Enhancing cached satellites with NASA mission data...');
            const enhancedSatellites = await nasaSatelliteService.enhanceSatellites(parsedCache.satellites);
            console.log(`✅ Enhanced ${enhancedSatellites.length} cached satellites with NASA data`);

            setSatellites(enhancedSatellites);
            satellitesRef.current = enhancedSatellites;
            setDataFetchError(false);
            setLoading(false);
            return;
          } else {
            console.log('Cache expired, fetching fresh data');
          }
        } catch (cacheError) {
          console.warn('Error reading cache:', cacheError);
        }
      }

      // SECOND: Check rate limit
      if (!canRefreshData()) {
        const remainingTime = getTimeUntilNextRefresh();
        const hours = Math.floor(remainingTime / (1000 * 60 * 60));
        const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
        console.log(`Rate limited: Next refresh available in ${hours}h ${minutes}m`);

        // Try fallback data if available
        const fallbackData = getFallbackTLEData();
        if (fallbackData) {
          console.log('Using fallback satellite data');
          const parsedFallback = parseTLEData(fallbackData);
          setSatellites(parsedFallback);
          satellitesRef.current = parsedFallback;
          setDataFetchError(false);
          setLoading(false);
          return;
        }

        setDataFetchError(true);
        setLoading(false);
        return;
      }

      // THIRD: Fetch fresh data from API
      console.log('🌐 Fetching fresh TLE data from API');

      // Update last fetch time
      const now = new Date();
      setLastFetchTime(now);
      setNextRefreshTime(new Date(now.getTime() + 3 * 60 * 60 * 1000)); // 3 hours from now

      // Use Firebase Function to fetch TLE data (bypasses CORS)
      const firebaseFunctionUrl = `https://gettledata-3ogooxwlda-uc.a.run.app?group=active&format=tle`;

      const response = await axios.get(firebaseFunctionUrl, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // Increased timeout for large dataset
      });

      if (!response.data || !response.data.success) {
        throw new Error('Firebase Function returned unsuccessful response');
      }

      const parsedSatellites = response.data.satellites.map(sat => ({
        ...sat,
        id: Math.random().toString(36).substr(2, 9)
      }));

      console.log(`📡 Received ${parsedSatellites.length} satellites from Firebase Function`);
      console.log(`Data source: ${response.data.source}`);

      // Cache the data for future use
      const cacheData = {
        satellites: parsedSatellites,
        timestamp: new Date().toISOString(),
        source: response.data.source
      };
      localStorage.setItem('tleData', JSON.stringify(cacheData));

      // Enhance satellites with NASA data
      console.log('🌌 Enhancing satellites with NASA mission data...');
      const enhancedSatellites = await nasaSatelliteService.enhanceSatellites(parsedSatellites);
      console.log(`✅ Enhanced ${enhancedSatellites.length} satellites with NASA data`);

      setSatellites(enhancedSatellites);
      satellitesRef.current = enhancedSatellites;
      setDataFetchError(false);
      setApiUnavailable(false);

      // FORCE IMMEDIATE SATELLITE UPDATE - bypass useEffect delay
      console.log('🚀 Forcing immediate satellite update after data fetch...');
      setTimeout(() => {
        if (viewerReady && parsedSatellites.length > 0) {
          console.log('🔄 Executing immediate satellite position update...');
          updateSatellitePositions(false);
        }
      }, 100); // Small delay to ensure state has updated

      setLoading(false);
      // Note: initialLoad remains true until progressive loading completes

    } catch (error) {
      console.error('❌ Error fetching TLE data:', error);

      // Try fallback data as last resort
      const fallbackData = getFallbackTLEData();
      if (fallbackData) {
        console.log('🔄 Using fallback satellite data due to API error');
        const parsedFallback = parseTLEData(fallbackData);
        setSatellites(parsedFallback);
        satellitesRef.current = parsedFallback;
        setDataFetchError(false);
        setLoading(false);
        return;
      }

      setDataFetchError(true);
      setLoading(false);
    } finally {
      // PERFORMANCE MONITORING: End timing
      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);
      console.log(`⏱️ TLE data fetch completed in ${duration} seconds`);
      setIsFetchingData(false);
    }
  };

  // Parse TLE data from string format
  const parseTLEData = (tleString) => {
    const lines = tleString.trim().split('\n');
    const satellites = [];

    for (let i = 0; i < lines.length; i += 3) {
      if (i + 2 < lines.length) {
        const name = lines[i].trim();
        const tle1 = lines[i + 1].trim();
        const tle2 = lines[i + 2].trim();

        satellites.push({
          name: name,
          tle: [tle1, tle2],
          id: Math.random().toString(36).substr(2, 9)
        });
      }
    }

    return satellites;
  };

  // Categorize satellite by type/company
  const categorizeSatellite = (name) => {
    const nameLower = name.toLowerCase();

    if (nameLower.includes('starlink')) return 'starlink';
    if (nameLower.includes('gps') || nameLower.includes('navstar')) return 'gps';
    if (nameLower.includes('iss') || nameLower.includes('zarya')) return 'iss';
    if (nameLower.includes('hubble')) return 'hubble';
    if (nameLower.includes('noaa') || nameLower.includes('goes')) return 'weather';
    if (nameLower.includes('terra') || nameLower.includes('aqua') || nameLower.includes('landsat')) return 'earth-observation';
    if (nameLower.includes('iridium')) return 'communication';
    if (nameLower.includes('oneweb')) return 'oneweb';
    if (nameLower.includes('ses') || nameLower.includes('astra') || nameLower.includes('eutelsat')) return 'communication';
    if (nameLower.includes('intelsat') || nameLower.includes('inmarsat')) return 'communication';
    if (nameLower.includes('galileo')) return 'galileo';
    if (nameLower.includes('beidou') || nameLower.includes('compass')) return 'beidou';
    if (nameLower.includes('glonass')) return 'glonass';

    return 'other';
  };

  // Filter satellites based on selected filter
  const getFilteredSatellites = () => {
    if (satelliteFilter === 'all') return satellites;
    if (satelliteFilter === 'featured') {
      // Return a curated selection of the most interesting satellites
      return satellites.filter(sat => {
        const category = categorizeSatellite(sat.name);
        // Include GPS, ISS, Weather, Communication, and Earth Observation satellites
        return ['gps', 'iss', 'weather', 'communication', 'earth-observation'].includes(category);
      });
    }
    if (satelliteFilter === 'nasa') {
      // Return only satellites with NASA mission data
      return satellites.filter(sat => sat.nasaData);
    }
    return satellites.filter(sat => categorizeSatellite(sat.name) === satelliteFilter);
  };

  // Calculate satellite position
  const calculateSatellitePosition = (tle) => {
    try {
      const satrec = satellite.twoline2satrec(tle[0], tle[1]);
      const now = new Date();
      const positionAndVelocity = satellite.propagate(satrec, now);

      // Check if propagation returned valid data
      if (positionAndVelocity && positionAndVelocity.position) {
        const positionEci = positionAndVelocity.position;
        const gmst = satellite.gstime(now);
        const positionGd = satellite.eciToGeodetic(positionEci, gmst);

        return {
          longitude: satellite.degreesLong(positionGd.longitude),
          latitude: satellite.degreesLat(positionGd.latitude),
          altitude: positionGd.height * 1000 // Convert to meters
        };
      }
    } catch (error) {
      console.warn('Error calculating satellite position:', error);
    }
    return null;
  };

  // Calculate orbital trajectory for a satellite
  const calculateTrajectory = (tle, durationMinutes = 120) => {
    const satrec = satellite.twoline2satrec(tle[0], tle[1]);
    const positions = [];
    const now = new Date();

    // Calculate positions every 30 seconds for the specified duration
    const stepSeconds = 30;
    const steps = (durationMinutes * 60) / stepSeconds;

    for (let i = 0; i <= steps; i++) {
      const time = new Date(now.getTime() + (i * stepSeconds * 1000));
      const positionAndVelocity = satellite.propagate(satrec, time);

      if (positionAndVelocity.position) {
        const positionEci = positionAndVelocity.position;
        const gmst = satellite.gstime(time);
        const positionGd = satellite.eciToGeodetic(positionEci, gmst);

        positions.push({
          longitude: satellite.degreesLong(positionGd.longitude),
          latitude: satellite.degreesLat(positionGd.latitude),
          altitude: positionGd.height * 1000,
          time: time
        });
      }
    }

    return positions;
  };

  // Check if satellite is visible in current camera view
  const isSatelliteVisible = (position, viewer) => {
    if (!position || !viewer) return false;

    try {
      const cartesianPos = Cesium.Cartesian3.fromDegrees(position.longitude, position.latitude, position.altitude);
      const screenPos = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesianPos);

      if (!screenPos) return false;

      const canvas = viewer.canvas;
      const padding = 50; // Add padding around viewport

      return screenPos.x >= -padding &&
             screenPos.x <= canvas.width + padding &&
             screenPos.y >= -padding &&
             screenPos.y <= canvas.height + padding;
    } catch (error) {
      return false;
    }
  };

  // Get camera zoom level for level-of-detail
  const getCameraZoomLevel = (viewer) => {
    if (!viewer) return 1;

    try {
      const camera = viewer.camera;
      const distance = Cesium.Cartesian3.distance(camera.position, camera.positionWC);
      // Normalize distance to zoom level (higher = more zoomed in)
      return Math.max(0.1, Math.min(1, 20000000 / distance));
    } catch (error) {
      return 1;
    }
  };

  // Progressive satellite loading with viewport culling
  const loadSatellitesBatch = async (batchIndex = 0) => {
    const currentViewer = viewerInstanceRef.current;
    if (!currentViewer || satellites.length === 0) {
      console.log('❌ Cannot load satellites: viewer or satellites not available');
      setIsProgressiveLoading(false);
      setRenderingSatellites(false);
      setInitialLoad(false);
      return;
    }

    const filteredSatellites = getFilteredSatellites();
    const batchSize = satelliteBatchSize;
    const startIndex = batchIndex * batchSize;
    const endIndex = Math.min(startIndex + batchSize, filteredSatellites.length);

    if (startIndex >= filteredSatellites.length) {
      console.log('✅ All satellite batches loaded - clearing loading states');
      // Clear all loading states when done
      setIsProgressiveLoading(false);
      setRenderingSatellites(false);
      setInitialLoad(false);
      setLoading(false); // Also clear the main loading state
      return;
    }

    console.log(`📦 Loading satellite batch ${batchIndex + 1}: ${startIndex}-${endIndex} of ${filteredSatellites.length}`);

    // Process batch
    for (let i = startIndex; i < endIndex; i++) {
      const sat = filteredSatellites[i];
      const position = calculateSatellitePosition(sat.tle);

      if (position && !isNaN(position.longitude) && !isNaN(position.latitude) && !isNaN(position.altitude)) {
        const category = categorizeSatellite(sat.name);

        // Color coding based on satellite type
        let color = Cesium.Color.YELLOW;
        switch (category) {
          case 'starlink': color = Cesium.Color.CYAN; break;
          case 'gps': color = Cesium.Color.GREEN; break;
          case 'iss': color = Cesium.Color.RED; break;
          case 'weather': color = Cesium.Color.ORANGE; break;
          case 'communication': color = Cesium.Color.PURPLE; break;
          case 'earth-observation': color = Cesium.Color.BLUE; break;
          default: color = Cesium.Color.YELLOW; break;
        }

        // Create unique ID
        const baseId = sat.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        let stableId = baseId;
        let counter = 1;
        while (currentViewer.entities.getById(stableId)) {
          stableId = `${baseId}_${counter}`;
          counter++;
        }

        // Get zoom level for level-of-detail
        const zoomLevel = getCameraZoomLevel(currentViewer);
        const pixelSize = Math.max(2, Math.min(8, 6 * zoomLevel)); // Scale point size with zoom

        // Create entity
        const entity = currentViewer.entities.add({
          id: stableId,
          name: sat.name,
          position: Cesium.Cartesian3.fromDegrees(
            position.longitude,
            position.latitude,
            position.altitude
          ),
          point: {
            pixelSize: pixelSize,
            color: color,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 1,
          },
          satelliteData: sat,
        });

          // Add description only for higher zoom levels to save memory
          if (zoomLevel > 0.5) {
            const noradId = sat.tle[0].split(' ')[1].replace(/[A-Z]$/, '');
            const inclination = parseFloat(sat.tle[1].split(' ')[2]).toFixed(2);
            const latStr = position.latitude >= 0 ? `${position.latitude.toFixed(2)}°N` : `${Math.abs(position.latitude).toFixed(2)}°S`;
            const lonStr = position.longitude >= 0 ? `${position.longitude.toFixed(2)}°E` : `${Math.abs(position.longitude).toFixed(2)}°W`;
            const altKm = (position.altitude / 1000).toFixed(1);

            // Build NASA mission information if available
            let nasaSection = '';
            if (sat.nasaData) {
              const nasa = sat.nasaData;
              const statusColor = nasaSatelliteService.getStatusColor(nasa.status);

              nasaSection = `
                <div style="border-top: 1px solid #777; margin: 8px 0; padding-top: 8px;">
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>🚀 NASA Mission Data:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Mission:</strong> ${nasa.mission || 'N/A'}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Status:</strong> <span style="color: ${statusColor};">${nasa.status || 'Unknown'}</span></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Organization:</strong> ${nasa.leadOrganization || 'N/A'}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Program:</strong> ${nasa.program || 'N/A'}</p>
                  ${nasa.description ? `<p style="margin: 0 0 4px 0; color: #cccccc; font-size: 11px;"><em>${nasa.description.substring(0, 100)}${nasa.description.length > 100 ? '...' : ''}</em></p>` : ''}
                  ${nasa.website ? `<p style="margin: 0 0 4px 0; color: #cccccc; font-size: 12px;"><a href="${nasa.website}" target="_blank" style="color: #4da6ff; text-decoration: none;">🌐 NASA Mission Page</a></p>` : ''}
                  <p style="margin: 0 0 4px 0; color: #888888; font-size: 10px;">Source: ${nasa.source || 'NASA'}</p>
                </div>
              `;
            }

            entity.description = `
              <div style="font-family: Arial, sans-serif; padding: 10px; color: white; max-width: 350px;">
                <h3 style="margin: 0 0 8px 0; color: white; font-size: 16px;">${sat.name}</h3>
                <p style="margin: 0 0 6px 0; color: #cccccc; font-size: 14px;">Type: ${category.replace('-', ' ').toUpperCase()}</p>
                <div style="border-top: 1px solid #555; margin: 8px 0; padding-top: 8px;">
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>Current Position:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">Latitude: ${latStr}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">Longitude: ${lonStr}</p>
                  <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 12px;">Altitude: ${altKm} km</p>
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>Orbital Data:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">NORAD ID: ${noradId}</p>
                  <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 12px;">Inclination: ${inclination}°</p>
                  <p style="margin: 0 0 8px 0; color: #cccccc; font-size: 12px;">
                    <a href="https://www.n2yo.com/satellite/?s=${noradId}" target="_blank" style="color: #4da6ff; text-decoration: none;">
                      📡 View on N2YO.com
                    </a>
                  </p>
                </div>
                ${nasaSection}
              </div>
            `;
          }
      }
    }

    // Load next batch after a small delay to prevent blocking
    setTimeout(() => {
      loadSatellitesBatch(batchIndex + 1);
    }, 50);
  };

  // Update satellite positions on the globe with optimizations
  const updateSatellitePositions = (isLiveUpdate = false) => {
    const currentViewer = viewerInstanceRef.current;

    // Comprehensive viewer validation
    if (!currentViewer) {
      console.log('⏸️ Skipping satellite position update - no viewer instance');
      return;
    }

    if (!currentViewer.entities) {
      console.log('⏸️ Skipping satellite position update - viewer has no entities collection');
      return;
    }

    if (typeof currentViewer.entities.getById !== 'function') {
      console.log('⏸️ Skipping satellite position update - entities.getById is not a function');
      return;
    }

    if (currentViewer.isDestroyed?.() === true) {
      console.log('⏸️ Skipping satellite position update - viewer is destroyed');
      return;
    }

    if (!viewerReady) {
      console.log('⏸️ Skipping satellite position update - viewer not marked as ready');
      return;
    }

    // Additional safety check - ensure viewer is actually ready
    try {
      // Try to access a basic property to ensure viewer is fully initialized
      const canvas = currentViewer.scene?.canvas;
      if (!canvas) {
        console.log('⏸️ Skipping satellite position update - viewer scene not ready');
        return;
      }
    } catch (error) {
      console.log('⏸️ Skipping satellite position update - viewer scene access error:', error.message);
      return;
    }

    // PERFORMANCE MONITORING: Start timing
    const updateStartTime = performance.now();

    try {
      // Clear existing satellite entities completely - optimized
      const clearStartTime = performance.now();
      console.log('🗑️ Clearing satellite entities...');

      // Use the most efficient approach - clear all entities at once
      try {
        // Get count before clearing for logging
        let satelliteCount = 0;
        currentViewer.entities.values.forEach(entity => {
          if (entity.id && !entity.id.startsWith('trajectory_') && !entity.id.startsWith('launch_')) {
            satelliteCount++;
          }
        });

        // Clear all satellite entities in one operation
        const entitiesToKeep = [];
        currentViewer.entities.values.forEach(entity => {
          if (entity.id && (entity.id.startsWith('trajectory_') || entity.id.startsWith('launch_'))) {
            entitiesToKeep.push(entity);
          }
        });

        // Remove all entities and re-add the ones we want to keep
        currentViewer.entities.removeAll();

        // Re-add trajectory and launch entities
        entitiesToKeep.forEach(entity => {
          try {
            currentViewer.entities.add(entity);
          } catch (error) {
            // Silently handle re-add errors
          }
        });

        console.log(`✅ Cleared ${satelliteCount} satellite entities, kept ${entitiesToKeep.length} trajectory entities`);
      } catch (error) {
        console.warn('Error during entity clearing:', error);
        // Fallback to individual removal
        currentViewer.entities.values.forEach(entity => {
          if (entity.id && !entity.id.startsWith('trajectory_') && !entity.id.startsWith('launch_')) {
            try {
              currentViewer.entities.remove(entity);
            } catch (removeError) {
              // Silently handle removal errors
            }
          }
        });
      }

      // Only add satellites if we have data and no error
      if (satellites.length === 0 || dataFetchError) {
        return;
      }

      // Get filtered satellites
      const filteredSatellites = getFilteredSatellites();

      // INSTANTANEOUS LOADING: Always load all satellites at once for immediate response
      console.log(`🚀 Instant loading: Loading ${filteredSatellites.length} satellites at once...`);
      setRenderingSatellites(true);

      // PERFORMANCE MONITORING: Start entity creation timing
      const entityCreationStartTime = performance.now();
      console.log(`🏗️ Starting entity creation for ${filteredSatellites.length} satellites...`);

      // Load all satellites in one go for instant response
      let entitiesCreated = 0;
      for (let i = 0; i < filteredSatellites.length; i++) {
        const sat = filteredSatellites[i];
        const position = calculateSatellitePosition(sat.tle);

        if (position && !isNaN(position.longitude) && !isNaN(position.latitude) && !isNaN(position.altitude)) {
          const category = categorizeSatellite(sat.name);

          // Color coding based on satellite type
          let color = Cesium.Color.YELLOW;
          switch (category) {
            case 'starlink': color = Cesium.Color.CYAN; break;
            case 'gps': color = Cesium.Color.GREEN; break;
            case 'iss': color = Cesium.Color.RED; break;
            case 'weather': color = Cesium.Color.ORANGE; break;
            case 'communication': color = Cesium.Color.PURPLE; break;
            case 'earth-observation': color = Cesium.Color.BLUE; break;
            default: color = Cesium.Color.YELLOW; break;
          }

          // Create unique ID
          const baseId = sat.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
          let stableId = baseId;
          let counter = 1;
          while (currentViewer.entities.getById(stableId)) {
            stableId = `${baseId}_${counter}`;
            counter++;
          }

          // Get zoom level for level-of-detail
          const zoomLevel = getCameraZoomLevel(currentViewer);
          const pixelSize = Math.max(2, Math.min(8, 6 * zoomLevel)); // Scale point size with zoom

          // Create entity
          const entity = currentViewer.entities.add({
            id: stableId,
            name: sat.name,
            position: Cesium.Cartesian3.fromDegrees(
              position.longitude,
              position.latitude,
              position.altitude
            ),
            point: {
              pixelSize: pixelSize,
              color: color,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 1,
            },
            satelliteData: sat,
          });

          // Add enhanced description with NASA data
          if (zoomLevel > 0.5) {
            const noradId = sat.tle[0].split(' ')[1].replace(/[A-Z]$/, '');
            const inclination = parseFloat(sat.tle[1].split(' ')[2]).toFixed(2);
            const latStr = position.latitude >= 0 ? `${position.latitude.toFixed(2)}°N` : `${Math.abs(position.latitude).toFixed(2)}°S`;
            const lonStr = position.longitude >= 0 ? `${position.longitude.toFixed(2)}°E` : `${Math.abs(position.longitude).toFixed(2)}°W`;
            const altKm = (position.altitude / 1000).toFixed(1);

            // Build NASA mission information if available
            let nasaSection = '';
            if (sat.nasaData) {
              const nasa = sat.nasaData;
              const statusColor = nasaSatelliteService.getStatusColor(nasa.status);

              nasaSection = `
                <div style="border-top: 1px solid #777; margin: 8px 0; padding-top: 8px;">
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>🚀 NASA Mission Data:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Mission:</strong> ${nasa.mission || 'N/A'}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Status:</strong> <span style="color: ${statusColor};">${nasa.status || 'Unknown'}</span></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Organization:</strong> ${nasa.leadOrganization || 'N/A'}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;"><strong>Program:</strong> ${nasa.program || 'N/A'}</p>
                  ${nasa.description ? `<p style="margin: 0 0 4px 0; color: #cccccc; font-size: 11px;"><em>${nasa.description.substring(0, 100)}${nasa.description.length > 100 ? '...' : ''}</em></p>` : ''}
                  ${nasa.website ? `<p style="margin: 0 0 4px 0; color: #cccccc; font-size: 12px;"><a href="${nasa.website}" target="_blank" style="color: #4da6ff; text-decoration: none;">🌐 NASA Mission Page</a></p>` : ''}
                  <p style="margin: 0 0 4px 0; color: #888888; font-size: 10px;">Source: ${nasa.source || 'NASA'}</p>
                </div>
              `;
            }

            entity.description = `
              <div style="font-family: Arial, sans-serif; padding: 10px; color: white; max-width: 350px;">
                <h3 style="margin: 0 0 8px 0; color: white; font-size: 16px;">${sat.name}</h3>
                <p style="margin: 0 0 6px 0; color: #cccccc; font-size: 14px;">Type: ${category.replace('-', ' ').toUpperCase()}</p>
                <div style="border-top: 1px solid #555; margin: 8px 0; padding-top: 8px;">
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>Current Position:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">Latitude: ${latStr}</p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">Longitude: ${lonStr}</p>
                  <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 12px;">Altitude: ${altKm} km</p>
                  <p style="margin: 0 0 4px 0; color: #cccccc; font-size: 13px;"><strong>Orbital Data:</strong></p>
                  <p style="margin: 0 0 2px 0; color: #ffffff; font-size: 12px;">NORAD ID: ${noradId}</p>
                  <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 12px;">Inclination: ${inclination}°</p>
                  <p style="margin: 0 0 8px 0; color: #cccccc; font-size: 12px;">
                    <a href="https://www.n2yo.com/satellite/?s=${noradId}" target="_blank" style="color: #4da6ff; text-decoration: none;">
                      📡 View on N2YO.com
                    </a>
                  </p>
                </div>
                ${nasaSection}
              </div>
            `;
          }

          entitiesCreated++;
        }
      }

      const entityCreationEndTime = performance.now();
      const entityCreationDuration = ((entityCreationEndTime - entityCreationStartTime) / 1000).toFixed(2);
      console.log(`✅ Created ${entitiesCreated} satellite entities in ${entityCreationDuration} seconds`);

      if (isLiveUpdate) {
        console.log(`✅ Live update complete: ${filteredSatellites.length} satellites updated`);
        setIsLiveUpdating(false);

        // If this is the first live update and we're still in initial load, complete it
        if (initialLoad && !firstUpdateCompletedRef.current) {
          console.log('🎯 First live update complete - clearing initial load');
          setInitialLoad(false);
          setLoading(false);
          firstUpdateCompletedRef.current = true; // Mark as completed
          // Exit early to prevent further processing in this update cycle
          return;
        }
      } else {
        console.log(`✅ Instant load complete: ${filteredSatellites.length} satellites loaded`);
      }

      setRenderingSatellites(false);

    } catch (error) {
      console.warn('Error in updateSatellitePositions:', error);
      setRenderingSatellites(false);
      setIsProgressiveLoading(false);
      setInitialLoad(false); // Make sure to clear loading state on error
    }

    // PERFORMANCE MONITORING: End timing
    const updateEndTime = performance.now();
    const updateDuration = ((updateEndTime - updateStartTime) / 1000).toFixed(2);
    console.log(`⏱️ Satellite position update completed in ${updateDuration} seconds`);
  };

  // Show trajectory for selected satellite
  const showTrajectory = (satelliteId) => {
    const currentViewer = viewerInstanceRef.current;
    console.log('Show trajectory called for satellite:', satelliteId);
    if (!currentViewer) {
      console.log('No viewer available');
      return;
    }

    // Remove existing trajectory
    if (trajectoryEntity) {
      currentViewer.entities.remove(trajectoryEntity);
      setTrajectoryEntity(null);
    }

    // Get satellite data directly from the entity
    const satelliteEntity = currentViewer.entities.getById(satelliteId);
    if (!satelliteEntity || !satelliteEntity.satelliteData) {
      console.log('Satellite entity not found or missing satellite data:', satelliteId);
      return;
    }

    const satellite = satelliteEntity.satelliteData;
    console.log('Found satellite:', satellite.name);

    try {
      // Calculate trajectory
      const trajectoryPoints = calculateTrajectory(satellite.tle, 90); // 90 minutes
      console.log('Calculated trajectory points:', trajectoryPoints.length);

      if (trajectoryPoints.length === 0) {
        console.log('No trajectory points calculated');
        return;
      }

      // Filter out invalid points and limit altitude for better rendering
      const validPoints = trajectoryPoints.filter(point =>
        point.longitude !== undefined &&
        point.latitude !== undefined &&
        point.altitude !== undefined &&
        !isNaN(point.longitude) &&
        !isNaN(point.latitude) &&
        !isNaN(point.altitude)
      );

      console.log('Valid trajectory points:', validPoints.length);

      if (validPoints.length < 2) {
        console.log('Not enough valid points for trajectory');
        return;
      }

      // Create trajectory polyline with actual satellite altitudes
      const positions = validPoints.map(point => {
        // Use actual altitude but cap extremely high values for rendering stability
        // Most satellites orbit between 500-40,000km, so 50,000km is a reasonable upper limit
        const actualAltitude = Math.min(point.altitude, 50000000); // Max 50,000km for stability
        return Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, actualAltitude);
      });

      console.log('Creating trajectory with positions:', positions.length);

      const trajectory = currentViewer.entities.add({
        id: `trajectory_${satelliteId}`,
        name: `Trajectory for ${satellite.name}`,
        polyline: {
          positions: positions,
          width: 3,
          material: Cesium.Color.LIME.withAlpha(0.9),
          clampToGround: false,
          arcType: Cesium.ArcType.NONE, // Straight lines for better performance
        },
      });

      setTrajectoryEntity(trajectory);
      setSelectedSatellite(satellite);

      console.log('Trajectory displayed for:', satellite.name);

      // Auto-hide trajectory after 30 seconds
      setTimeout(() => {
        if (trajectoryEntity && trajectoryEntity.id === `trajectory_${satelliteId}`) {
          console.log('Auto-hiding trajectory for:', satellite.name);
          currentViewer.entities.remove(trajectoryEntity);
          setTrajectoryEntity(null);
          setSelectedSatellite(null);
        }
      }, 30000);

    } catch (error) {
      console.error('Error creating trajectory:', error);
    }
  };

  // Make showTrajectory available globally for onclick handlers
  useEffect(() => {
    // Only set up the function once when component mounts, not on every satellite change
    if (!window.showTrajectory) {
      console.log('Setting up global showTrajectory function');

      window.showTrajectory = (satelliteId) => {
        const currentViewer = viewerInstanceRef.current;
        console.log('Show trajectory called for satellite:', satelliteId);

        if (!currentViewer) {
          console.log('No viewer available');
          return;
        }

        // Remove existing trajectory
        if (trajectoryEntity) {
          currentViewer.entities.remove(trajectoryEntity);
          setTrajectoryEntity(null);
        }

        // Get satellite data directly from the entity
        const satelliteEntity = currentViewer.entities.getById(satelliteId);
        if (!satelliteEntity || !satelliteEntity.satelliteData) {
          console.log('Satellite entity not found or missing satellite data:', satelliteId);
          return;
        }

        const satellite = satelliteEntity.satelliteData;
        console.log('Found satellite:', satellite.name);

        try {
          // Calculate trajectory
          const trajectoryPoints = calculateTrajectory(satellite.tle, 90); // 90 minutes
          console.log('Calculated trajectory points:', trajectoryPoints.length);

          if (trajectoryPoints.length === 0) {
            console.log('No trajectory points calculated');
            return;
          }

          // Filter out invalid points and limit altitude for better rendering
          const validPoints = trajectoryPoints.filter(point =>
            point.longitude !== undefined &&
            point.latitude !== undefined &&
            point.altitude !== undefined &&
            !isNaN(point.longitude) &&
            !isNaN(point.latitude) &&
            !isNaN(point.altitude)
          );

          console.log('Valid trajectory points:', validPoints.length);

          if (validPoints.length < 2) {
            console.log('Not enough valid points for trajectory');
            return;
          }

          // Create trajectory polyline with actual satellite altitudes
          const positions = validPoints.map(point => {
            // Use actual altitude but cap extremely high values for rendering stability
            // Most satellites orbit between 500-40,000km, so 50,000km is a reasonable upper limit
            const actualAltitude = Math.min(point.altitude, 50000000); // Max 50,000km for stability
            return Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, actualAltitude);
          });

          console.log('Creating trajectory with positions:', positions.length);

          const trajectory = currentViewer.entities.add({
            id: `trajectory_${satelliteId}`,
            name: `Trajectory for ${satellite.name}`,
            polyline: {
              positions: positions,
              width: 3,
              material: Cesium.Color.LIME.withAlpha(0.9),
              clampToGround: false,
              arcType: Cesium.ArcType.NONE, // Straight lines for better performance
            },
          });

          setTrajectoryEntity(trajectory);

          console.log('Trajectory displayed for:', satellite.name);

          // Auto-hide trajectory after 30 seconds
          setTimeout(() => {
            if (trajectory && trajectory.id === `trajectory_${satelliteId}`) {
              console.log('Auto-hiding trajectory for:', satellite.name);
              currentViewer.entities.remove(trajectory);
              setTrajectoryEntity(null);
            }
          }, 30000);

        } catch (error) {
          console.error('Error creating trajectory:', error);
        }
      };
    }

    return () => {
      // Don't delete the function on cleanup to avoid recreating it
    };
  }, [trajectoryEntity]); // Include trajectoryEntity dependency

  // Update satellite positions on the globe
  useEffect(() => {
    console.log('🔄 useEffect triggered - checking conditions...');
    console.log(`   satellites.length: ${satellites.length}`);
    console.log(`   viewerReady: ${viewerReady}`);
    console.log(`   satelliteFilter: ${satelliteFilter}`);

    // Skip during hot module replacement in development - but allow satellite updates
    if (process.env.NODE_ENV === 'development' && module.hot && satellites.length === 0) {
      console.log('⏸️ Skipping due to hot module replacement (no satellites yet)');
      return;
    }

    // Only run if we have satellites and viewer is ready
    if (satellites.length > 0 && viewerReady) {
      console.log('✅ Conditions met - proceeding with satellite update...');
      const currentViewer = viewerInstanceRef.current;

      // Comprehensive validation before proceeding
      if (!currentViewer) {
        console.log('⏸️ No viewer instance available');
        return;
      }

      if (!currentViewer.entities) {
        console.log('⏸️ Viewer entities not available');
        return;
      }

      if (typeof currentViewer.entities.getById !== 'function') {
        console.log('⏸️ Viewer entities.getById not available');
        return;
      }

      if (currentViewer.isDestroyed?.() === true) {
        console.log('⏸️ Viewer has been destroyed');
        return;
      }

      try {
        // Test if viewer is actually functional by trying to access a basic property
        const canvas = currentViewer.scene?.canvas;
        if (!canvas) {
          console.log('⏸️ Viewer scene canvas not available');
          return;
        }

        console.log('🔄 Satellite/filter change detected, updating positions...');

        // If this is the first load and we have satellites, mark initialLoad as complete
        if (initialLoad && satellites.length > 0) {
          console.log('🎯 First load complete - transitioning to live updates');
          setInitialLoad(false);
          setLoading(false);
        }

        updateSatellitePositions(false); // Explicitly mark as initial load
      } catch (error) {
        console.log('⏸️ Viewer validation failed:', error.message);
      }
    } else {
      console.log('⏸️ Conditions not met - waiting...');
    }
  }, [satellites, satelliteFilter, viewerReady]);



  // Update satellite positions periodically based on selected frequency
  useEffect(() => {
    if (!viewerReady || satellites.length === 0 || dataFetchError) return;

    const interval = setInterval(() => {
      // Double-check viewer is still valid before updating
      const currentViewer = viewerInstanceRef.current;
      if (!currentViewer ||
          !currentViewer.entities ||
          typeof currentViewer.entities.getById !== 'function' ||
          currentViewer.isDestroyed?.() === true ||
          !viewerReady) {
        console.log('⏸️ Skipping periodic update - viewer not ready');
        return;
      }

      updateSatellitePositions(true); // Pass true for live update
    }, UPDATE_FREQUENCIES[updateFrequency]); // Use selected frequency

    return () => clearInterval(interval);
  }, [viewerReady, satellites.length, dataFetchError, satelliteFilter, updateFrequency]);

  useEffect(() => {
    // Set up Cesium viewer
    if (viewerRef.current && !viewerInstanceRef.current) {
      console.log('🏗️ Starting Cesium viewer initialization...');
      const viewerStartTime = performance.now();

      try {
        const cesiumViewer = new Cesium.Viewer(viewerRef.current, {
          terrainProvider: new Cesium.EllipsoidTerrainProvider(),
          baseLayerPicker: false,
          geocoder: false,
          homeButton: false,
          sceneModePicker: false,
          timeline: false,
          animation: false,
          navigationHelpButton: false,
          fullscreenButton: false,
          infoBox: true,
          selectionIndicator: true,
          skyBox: false,
          skyAtmosphere: false, // Disable sky atmosphere for space view
          sun: true, // Enable sun for realistic lighting
          moon: true, // Enable moon
        });

        const viewerEndTime = performance.now();
        console.log(`⏱️ Cesium viewer initialization completed in ${(viewerEndTime - viewerStartTime).toFixed(3)} seconds`);

        // Store viewer instance immediately and verify it's valid
        viewerInstanceRef.current = cesiumViewer;

        // Verify viewer was created successfully
        if (!cesiumViewer) {
          console.error('❌ Failed to create Cesium viewer');
          return;
        }

        if (!cesiumViewer.entities) {
          console.error('❌ Cesium viewer created but entities collection is missing');
          return;
        }

        console.log('✅ Cesium viewer created successfully with entities collection');

        // Set initial camera position
        cesiumViewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(-75.0, 40.0, 10000000.0),
        });

        // Add screen space event handler for satellite clicks
        const handler = new Cesium.ScreenSpaceEventHandler(cesiumViewer.scene.canvas);

        // Left click to select satellite and show trajectory
        handler.setInputAction((click) => {
          const pickedObject = cesiumViewer.scene.pick(click.position);
          if (Cesium.defined(pickedObject) && pickedObject.id && pickedObject.id.id) {
            const satelliteId = pickedObject.id.id;
            console.log('Satellite clicked:', satelliteId);
            showTrajectory(satelliteId);
          }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        // Mark viewer as ready immediately since we've validated the viewer
        setViewerReady(true);
        console.log('🎯 Cesium viewer marked as ready');

        // Check for trajectory data in URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const trajectoryParam = urlParams.get('trajectory');
        const modeParam = urlParams.get('mode');

        if (trajectoryParam) {
          setTrajectoryMode(true);
          setLoading(false); // Skip satellite loading in trajectory mode

          try {
            const trajectoryData = JSON.parse(decodeURIComponent(trajectoryParam));
            console.log('Loading trajectory from URL:', trajectoryData);

            // Use requestAnimationFrame for better performance
            requestAnimationFrame(() => {
              setTimeout(() => {
                showLaunchTrajectory(trajectoryData);
              }, 100); // Reduced delay
            });
          } catch (error) {
            console.error('Error parsing trajectory data from URL:', error);
          }
        } else if (modeParam === 'trajectory') {
          setTrajectoryMode(true);
          setLoading(false); // Skip satellite loading in trajectory mode
        } else {
          // Normal satellite tracker mode
          setTrajectoryMode(false);

          // Use requestAnimationFrame to defer heavy operations
          requestAnimationFrame(() => {
            console.log('🎯 Starting satellite data fetch after viewer initialization...');
            fetchTLEData();
          });
        }

        // Clean up on unmount
        return () => {
          if (cesiumViewer && !cesiumViewer.isDestroyed()) {
            console.log('🧹 Cleaning up Cesium viewer');
            cesiumViewer.destroy();
            viewerInstanceRef.current = null;
          }
        };
      } catch (error) {
        console.error('❌ Error creating Cesium viewer:', error);
        setDataFetchError(true);
        setLoading(false);
      }
    }
  }, []);

  // Render launch trajectory on the globe
  const renderLaunchTrajectory = (trajectoryData) => {
    const currentViewer = viewerInstanceRef.current;
    if (!currentViewer) {
      console.error('No Cesium viewer available for trajectory rendering');
      return false;
    }

    try {
      console.log('Starting trajectory rendering for:', trajectoryData?.metadata?.missionName);

      // Validate trajectory data
      if (!trajectoryData || !trajectoryData.positions || !Array.isArray(trajectoryData.positions)) {
        console.error('Invalid trajectory data structure:', trajectoryData);
        return false;
      }

      if (trajectoryData.positions.length === 0) {
        console.error('No positions in trajectory data');
        return false;
      }

      // Clear existing launch trajectories
      const entitiesToRemove = [];
      currentViewer.entities.values.forEach(entity => {
        if (entity.id && (
          entity.id.startsWith('launch_trajectory_') ||
          entity.id.startsWith('launch_pad_') ||
          entity.id.startsWith('orbital_insertion_') ||
          entity.id.startsWith('launch_info_')
        )) {
          entitiesToRemove.push(entity);
        }
      });
      entitiesToRemove.forEach(entity => {
        try {
          currentViewer.entities.remove(entity);
        } catch (removeError) {
          console.warn('Error removing entity:', entity.id, removeError);
        }
      });

      // Get formatted trajectory data
      let formattedTrajectory;
      try {
        formattedTrajectory = trajectoryCalculator.getFormattedTrajectory(trajectoryData);
        console.log('Formatted trajectory phases:', formattedTrajectory?.phases?.length);
      } catch (formatError) {
        console.error('Error formatting trajectory:', formatError);
        return false;
      }

      if (!formattedTrajectory || !formattedTrajectory.phases || !Array.isArray(formattedTrajectory.phases)) {
        console.error('Invalid formatted trajectory:', formattedTrajectory);
        return false;
      }

      // SIMPLIFIED: Create ONE CONTINUOUS trajectory from ALL positions
      console.log('🎯 Creating SINGLE CONTINUOUS trajectory line...');

      let allPositions = [];
      let phaseCount = 0;

      // Collect ALL positions from ALL phases without filtering
      formattedTrajectory.phases.forEach((phase, phaseIndex) => {
        if (phase.positions && Array.isArray(phase.positions)) {
          console.log(`📊 Phase ${phaseIndex} (${phase.name}): ${phase.positions.length} positions`);

          // Add ALL positions (don't filter out any)
          phase.positions.forEach(pos => {
            allPositions.push({
              longitude: Number(pos.longitude) || 0,
              latitude: Number(pos.latitude) || 0,
              altitude: Math.max(1000, Number(pos.altitude) || 1000),
              phaseIndex: phaseIndex,
              phaseName: phase.name,
              phaseColor: phase.color
            });
          });

          phaseCount++;
        }
      });

      console.log(`📈 Total positions collected: ${allPositions.length} from ${phaseCount} phases`);

      if (allPositions.length < 2) {
        console.error('❌ Not enough positions for trajectory');
        return false;
      }

      // Create Cesium positions array
      const cesiumPositions = [];
      allPositions.forEach((pos, index) => {
        try {
          const cartesianPos = Cesium.Cartesian3.fromDegrees(
            pos.longitude,
            pos.latitude,
            pos.altitude
          );
          cesiumPositions.push(cartesianPos);

          // Debug first few and last few positions
          if (index < 3 || index > allPositions.length - 4) {
            console.log(`🔍 Position ${index}: lat=${pos.latitude.toFixed(2)}, lon=${pos.longitude.toFixed(2)}, alt=${(pos.altitude/1000).toFixed(1)}km`);
          }
        } catch (error) {
          console.warn(`❌ Error converting position ${index}:`, error);
        }
      });

      console.log(`✅ Created ${cesiumPositions.length} Cesium positions`);

      if (cesiumPositions.length >= 2) {
        // Create SINGLE trajectory entity with all positions
        const trajectoryEntity = currentViewer.entities.add({
          id: `single_trajectory_${Date.now()}`,
          name: `${trajectoryData.metadata?.missionName || 'Unknown'} - Complete Trajectory`,
          polyline: {
            positions: cesiumPositions,
            width: 6,
            material: Cesium.Color.ORANGE.withAlpha(0.9),
            clampToGround: false,
            arcType: Cesium.ArcType.NONE,
          },
        });

        console.log(`🚀 SUCCESS: Single continuous trajectory created with ${cesiumPositions.length} points`);
      } else {
        console.error('❌ Not enough valid Cesium positions');
        return false;
      }

      // Add key markers
      if (allPositions.length > 0) {
        // Launch pad marker
        try {
          const launchPadPos = Cesium.Cartesian3.fromDegrees(
            allPositions[0].longitude,
            allPositions[0].latitude,
            Math.max(1000, allPositions[0].altitude)
          );

          currentViewer.entities.add({
            id: `launch_pad_${trajectoryData.metadata?.missionName || 'unknown'}_${Date.now()}`,
            name: 'Launch Pad',
            position: launchPadPos,
            point: {
              pixelSize: 12,
              color: Cesium.Color.RED,
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 2,
            },
            label: {
              text: '🚀',
              font: '20pt sans-serif',
              fillColor: Cesium.Color.WHITE,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 2,
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -32),
            }
          });
        } catch (markerError) {
          console.warn('Error adding launch pad marker:', markerError);
        }

        // Orbital insertion marker
        try {
          const finalPos = allPositions[allPositions.length - 1];
          const orbitalPos = Cesium.Cartesian3.fromDegrees(
            finalPos.longitude,
            finalPos.latitude,
            Math.max(1000, finalPos.altitude)
          );

          currentViewer.entities.add({
            id: `orbital_insertion_${trajectoryData.metadata?.missionName || 'unknown'}_${Date.now()}`,
            name: 'Orbital Insertion',
            position: orbitalPos,
            point: {
              pixelSize: 10,
              color: Cesium.Color.GREEN,
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 2,
            },
            label: {
              text: '🛰️',
              font: '16pt sans-serif',
              fillColor: Cesium.Color.WHITE,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 2,
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -28),
            }
          });
        } catch (markerError) {
          console.warn('Error adding orbital insertion marker:', markerError);
        }
      }

      // Add trajectory info panel
      try {
        // Find a valid midpoint for the info panel
        let midPoint = null;
        const midIndex = Math.floor(trajectoryData.positions.length / 2);

        // Try the middle position first
        if (trajectoryData.positions[midIndex] &&
            !isNaN(Number(trajectoryData.positions[midIndex].longitude)) &&
            !isNaN(Number(trajectoryData.positions[midIndex].latitude))) {
          midPoint = trajectoryData.positions[midIndex];
        } else {
          // Find any valid position
          for (const pos of trajectoryData.positions) {
            if (pos &&
                !isNaN(Number(pos.longitude)) &&
                !isNaN(Number(pos.latitude)) &&
                isFinite(Number(pos.longitude)) &&
                isFinite(Number(pos.latitude))) {
              midPoint = pos;
              break;
            }
          }
        }

        if (midPoint) {
          const longitude = Number(midPoint.longitude);
          const latitude = Number(midPoint.latitude);
          const altitude = Math.max(1000, Number(midPoint.altitude) || 100000);

          if (!isNaN(longitude) && !isNaN(latitude) && !isNaN(altitude) &&
              longitude >= -180 && longitude <= 180 &&
              latitude >= -90 && latitude <= 90) {

            const infoPosition = Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude);

            const infoEntity = currentViewer.entities.add({
              id: `launch_info_${trajectoryData.metadata?.missionName || 'unknown'}_${Date.now()}`,
              name: `${trajectoryData.metadata?.missionName || 'Unknown'} Trajectory Info`,
              position: infoPosition,
              label: {
                text: `${trajectoryData.metadata?.missionName || 'Unknown Mission'}`,
                font: '12pt sans-serif',
                fillColor: Cesium.Color.YELLOW,
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 2,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                verticalOrigin: Cesium.VerticalOrigin.TOP,
                horizontalOrigin: Cesium.VerticalOrigin.LEFT,
                pixelOffset: new Cesium.Cartesian2(20, 0),
              }
            });

            console.log('Successfully added trajectory info panel');
          } else {
            console.warn('Invalid coordinates for trajectory info panel:', { longitude, latitude, altitude });
          }
        } else {
          console.warn('No valid position found for trajectory info panel');
        }
      } catch (infoError) {
        console.warn('Error adding trajectory info panel:', infoError);
      }

      console.log(`Launch trajectory rendering completed for ${trajectoryData.metadata?.missionName || 'Unknown'}`);
      return true;
    } catch (error) {
      console.error('Critical error rendering launch trajectory:', error);
      console.error('Trajectory data:', trajectoryData);
      console.error('Error stack:', error.stack);
      return false;
    }
  };

  // Show launch trajectory for a specific launch
  const showLaunchTrajectory = (launchData) => {
    try {
      console.log('Calculating trajectory for launch:', launchData.name);

      // Extract launch data for trajectory calculation
      const trajectoryInput = {
        launchPad: {
          latitude: launchData.pad?.latitude || 28.5619, // Default to Cape Canaveral
          longitude: launchData.pad?.longitude || -80.5773,
          name: launchData.pad?.location?.name || 'Unknown Launch Site'
        },
        rocket: {
          name: launchData.rocket?.configuration?.name || 'Unknown Rocket',
          stages: 2
        },
        mission: {
          name: launchData.name || 'Unknown Mission'
        },
        targetOrbit: {
          altitude: 408000, // Default to ISS altitude (408km)
          inclination: 28.5 // Default inclination
        }
      };

      // Calculate trajectory
      const trajectory = trajectoryCalculator.calculateLaunchTrajectory(trajectoryInput);

      if (trajectory && trajectory.positions.length > 0) {
        // Render trajectory on globe
        const success = renderLaunchTrajectory(trajectory);
        if (success) {
          setSelectedLaunchTrajectory(trajectory);
          setLaunchTrajectories(prev => [...prev.filter(t => t.metadata.missionName !== trajectory.metadata.missionName), trajectory]);

          // Center camera on launch trajectory
          const currentViewer = viewerInstanceRef.current;
          if (currentViewer) {
            const midPoint = trajectory.positions[Math.floor(trajectory.positions.length / 2)];
            currentViewer.camera.setView({
              destination: Cesium.Cartesian3.fromDegrees(midPoint.longitude, midPoint.latitude, 2000000.0),
            });
          }
        }
      }
    } catch (error) {
      console.error('Error showing launch trajectory:', error);
    }
  };



  // Center camera on user's location
  const centerOnUserLocation = () => {
    const currentViewer = viewerInstanceRef.current;
    if (navigator.geolocation && currentViewer) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          currentViewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, 5000000.0),
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
          // Default to a central location if geolocation fails
          currentViewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(0, 0, 10000000.0),
          });
        }
      );
    }
  };

  return (
    <div className="w-full h-[calc(100vh-80px)] relative">
      <div ref={viewerRef} className="w-full h-full" />

      {/* Control Panel */}
      <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-4 rounded-lg min-w-64">
        <h2 className="text-xl font-bold mb-3">
          {trajectoryMode ? '🚀 Launch Trajectory Viewer' : 'Satellite Tracker'}
        </h2>

        {trajectoryMode ? (
          // Trajectory Mode UI
          <div className="space-y-2">
            <p className="text-sm text-green-400">
              🎯 Trajectory Mode Active
            </p>
            <p className="text-xs text-gray-400">
              Focused view for launch trajectories
            </p>

            {/* Trajectory Legend */}
            <div className="mt-4 space-y-2">
              <h3 className="text-sm font-semibold text-gray-300">Trajectory Markers</h3>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-gray-300">🚀 Launch Pad</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-300">🛰️ Orbital Insertion</span>
                </div>
              </div>
            </div>

            {/* Switch to Satellite Tracker Mode Button */}
            <div className="mt-4">
              <button
                onClick={() => {
                  // Clear trajectory mode and switch to satellite tracker
                  setTrajectoryMode(false);
                  setLaunchTrajectories([]);
                  setSelectedLaunchTrajectory(null);

                  // Clear any trajectory entities
                  const currentViewer = viewerInstanceRef.current;
                  if (currentViewer) {
                    const entitiesToRemove = [];
                    currentViewer.entities.values.forEach(entity => {
                      if (entity.id && (
                        entity.id.startsWith('launch_trajectory_') ||
                        entity.id.startsWith('launch_pad_') ||
                        entity.id.startsWith('orbital_insertion_') ||
                        entity.id.startsWith('launch_info_') ||
                        entity.id.startsWith('single_trajectory_') ||
                        entity.id.startsWith('phase_marker_')
                      )) {
                        entitiesToRemove.push(entity);
                      }
                    });
                    entitiesToRemove.forEach(entity => currentViewer.entities.remove(entity));
                  }

                  // Navigate to satellite tracker
                  window.location.href = '/globe';
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                🛰️ Switch to Satellite Tracker
              </button>
            </div>
          </div>
        ) : (
          // Normal Satellite Tracker UI
          <>
            {loading ? (
              <p className="text-sm text-gray-300">Loading satellite data...</p>
            ) : dataFetchError ? (
              <div className="space-y-2">
                <p className="text-sm text-red-400">
                  ⚠️ Unable to fetch satellite data
                </p>
                <p className="text-xs text-gray-400">
                  Data could not be fetched from external sources. Please try again later.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-semibold text-yellow-400">{satellites.length.toLocaleString()}</span> satellites loaded
                </p>
                <p className="text-xs text-gray-400">
                  Positions update every {updateFrequency === '30s' ? '30 seconds' : updateFrequency === '60s' ? '60 seconds' : updateFrequency === '5m' ? '5 minutes' : '30 minutes'}
                </p>
              </div>
            )}

            {/* Satellite Filter */}
            <div className="mt-4 space-y-2">
              <label className="text-xs text-gray-300 block">Filter by Type:</label>
              <select
                value={satelliteFilter}
                onChange={(e) => {
                  const newFilter = e.target.value;
                  if (newFilter !== satelliteFilter) { // Only update if filter actually changed
                    setSatelliteFilter(newFilter);
                    // Save filter to localStorage for persistence
                    localStorage.setItem('satelliteFilter', newFilter);
                    setSelectedSatellite(null); // Clear selection when filtering
                    if (trajectoryEntity) {
                      const currentViewer = viewerInstanceRef.current;
                      if (currentViewer) {
                        currentViewer.entities.remove(trajectoryEntity);
                        setTrajectoryEntity(null);
                      }
                    }
                  }
                }}
                className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="featured">⭐ Featured ({satellites.filter(s => ['gps', 'iss', 'weather', 'communication', 'earth-observation'].includes(categorizeSatellite(s.name))).length})</option>
                <option value="all">All ({satellites.length.toLocaleString()})</option>
                <option value="nasa">🚀 NASA Missions ({satellites.filter(s => s.nasaData).length})</option>
                <option value="starlink">Starlink ({satellites.filter(s => categorizeSatellite(s.name) === 'starlink').length})</option>
                <option value="gps">GPS ({satellites.filter(s => categorizeSatellite(s.name) === 'gps').length})</option>
                <option value="iss">ISS ({satellites.filter(s => categorizeSatellite(s.name) === 'iss').length})</option>
                <option value="weather">Weather ({satellites.filter(s => categorizeSatellite(s.name) === 'weather').length})</option>
                <option value="communication">Comm ({satellites.filter(s => categorizeSatellite(s.name) === 'communication').length})</option>
                <option value="earth-observation">Earth Obs ({satellites.filter(s => categorizeSatellite(s.name) === 'earth-observation').length})</option>
                <option value="other">Other ({satellites.filter(s => categorizeSatellite(s.name) === 'other').length})</option>
              </select>
            </div>

            {/* Update Frequency Selector */}
            <div className="mt-4 space-y-2">
              <label className="text-xs text-gray-300 block">Update Frequency:</label>
              <select
                value={updateFrequency}
                onChange={(e) => {
                  const newFrequency = e.target.value;
                  if (newFrequency !== updateFrequency) {
                    setUpdateFrequency(newFrequency);
                    console.log(`🔄 Update frequency changed to: ${newFrequency}`);
                  }
                }}
                className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="30s">30 seconds</option>
                <option value="60s">60 seconds</option>
                <option value="5m">5 minutes</option>
                <option value="30m">30 minutes</option>
              </select>
            </div>

            <div className="mt-4 flex flex-col space-y-2">
              <button
                onClick={centerOnUserLocation}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                📍 My Location
              </button>

              <button
                onClick={() => {
                  const currentViewer = viewerInstanceRef.current;
                  if (currentViewer) {
                    currentViewer.camera.setView({
                      destination: Cesium.Cartesian3.fromDegrees(-75.0, 40.0, 10000000.0),
                    });
                  }
                }}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                🌍 Reset View
              </button>

              <button
                onClick={() => {
                  const currentViewer = viewerInstanceRef.current;
                  if (currentViewer) {
                    // Clear all trajectory entities
                    const entitiesToRemove = [];
                    currentViewer.entities.values.forEach(entity => {
                      if (entity.id && entity.id.startsWith('trajectory_')) {
                        entitiesToRemove.push(entity);
                      }
                    });
                    entitiesToRemove.forEach(entity => {
                      currentViewer.entities.remove(entity);
                    });
                    // Clear trajectory state
                    setTrajectoryEntity(null);
                    setSelectedSatellite(null);
                    console.log('All trajectories cleared');
                  }
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                🗑️ Clear Trajectories
              </button>
            </div>
          </>
        )}

        {/* Only show these controls in normal satellite tracker mode */}
        {!trajectoryMode && (
          <>
            {/* Satellite Legend */}
            <div className="mt-4 space-y-2">
              <h3 className="text-sm font-semibold text-gray-300">Legend</h3>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <span className="text-gray-300">Other Satellites</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
                  <span className="text-gray-300">Starlink</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="text-gray-300">GPS</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-gray-300">ISS</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                  <span className="text-gray-300">Weather</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                  <span className="text-gray-300">Communication</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                  <span className="text-gray-300">Earth Observation</span>
                </div>
              </div>
            </div>

            {/* Launch Trajectory Legend */}
            {launchTrajectories.length > 0 && (
              <div className="mt-4 space-y-2">
                <h3 className="text-sm font-semibold text-gray-300">Trajectory Phases</h3>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-1 bg-red-500"></div>
                    <span className="text-gray-300">Vertical Climb</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-1 bg-orange-500"></div>
                    <span className="text-gray-300">Gravity Turn</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-1 bg-yellow-500"></div>
                    <span className="text-gray-300">Main Engine Cutoff</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-1 bg-blue-500"></div>
                    <span className="text-gray-300">Upper Stage to Orbit</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-gray-300">🚀 Launch Pad</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-300">🛰️ Orbital Insertion</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Loading Notice - Only show in normal mode */}
      {(!trajectoryMode && (initialLoad || renderingSatellites)) && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-90 text-white p-6 rounded-lg text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold mb-2">
            {initialLoad ? 'Loading Satellites...' : 'Updating Positions...'}
          </h3>
          <p className="text-sm text-gray-300 mb-2">
            {initialLoad ? 'Fetching satellite data from server' : 'Updating satellite positions on the globe'}
          </p>
          <p className="text-xs text-gray-400">
            {initialLoad ? 'Using cached data when available' : `Positions update every ${updateFrequency === '30s' ? '30 seconds' : updateFrequency === '60s' ? '60 seconds' : updateFrequency === '5m' ? '5 minutes' : '30 minutes'}`}
          </p>
        </div>
      )}

      {/* Trajectory Loading Notice - Only show in trajectory mode */}
      {trajectoryMode && !launchTrajectories.length && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-90 text-white p-6 rounded-lg text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold mb-2">Loading Trajectory...</h3>
          <p className="text-sm text-gray-300 mb-2">
            Calculating launch trajectory and rendering on globe
          </p>
          <p className="text-xs text-gray-400">
            This may take a few seconds for complex trajectories
          </p>
        </div>
      )}


    </div>
  );
};

export default GlobeVisualizer;
